import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Loader2, ArrowLeft, CreditCard, Users2, Layers, Building2, Save, Coins, Sparkles, Activity, TrendingUp, BadgeCheck, Zap, FileText, Pencil, X, Check } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../context/AdminAuthContext';
import { subsGet, subsUpdate, subsAiSummary, subsStats } from '../lib/marketingApi';

const STATUS_STYLE = {
  active: 'bg-emerald-100 text-emerald-700', trial: 'bg-sky-100 text-sky-700',
  past_due: 'bg-amber-100 text-amber-700', cancelled: 'bg-rose-100 text-rose-600',
};
const ROLE_STYLE = { owner: 'bg-[#0035A8] text-white', admin: 'bg-[#0035A8]/10 text-[#0035A8]', user: 'bg-slate-100 text-slate-600' };
const fmtUSD = (n) => `$${Math.round(n || 0).toLocaleString('en-US')}`;
const fmtNum = (n) => (n ?? 0).toLocaleString('en-US');
function fmt(iso, withTime) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', withTime ? { dateStyle: 'medium', timeStyle: 'short' } : { dateStyle: 'medium' }); } catch (e) { return iso; }
}

function Metric({ label, value, sub, accent }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`mt-1 text-xl font-bold ${accent || 'text-slate-900'}`}>{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-slate-400">{sub}</div> : null}
    </div>
  );
}

export default function SubscriptionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const [d, setD] = useState(null);
  const [tab, setTab] = useState('overview');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const load = () => subsGet(id).then(setD).catch(() => toast.error('Failed to load customer'));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { subsStats().then((s) => setIndustries(s.industries || [])).catch(() => {}); }, []);

  const startEdit = () => {
    const acc = (d && d.account) || {};
    setEdit({ owner: acc.owner || '', segment: acc.segment || '', size: acc.size || '', industry: acc.industry || '' });
    setEditing(true);
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try { await subsUpdate(id, edit); toast.success('CRM details updated'); setEditing(false); load(); }
    catch (e) { toast.error('Update failed'); } finally { setSavingEdit(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { await subsUpdate(id, { note }); setNote(''); toast.success('Note added'); load(); }
    catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };

  const genAi = async () => {
    setAiLoading(true);
    try { const r = await subsAiSummary(id); r.note ? toast.success('AI note generated') : toast.error('Could not generate (LLM unavailable)'); load(); }
    catch (e) { toast.error('Could not generate'); } finally { setAiLoading(false); }
  };

  if (!d) return (<div className="min-h-screen bg-slate-50"><AdminHeader active="subscriptions" /><div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div></div>);

  const a = d.account || {};
  const act = a.activity || null;
  const users = d.users || [];
  const plans = d.plan_events || [];
  const credits = d.credit_events || [];
  const engagement = d.engagement || [];
  const sites = d.sites || [];

  const chartData = [...engagement].reverse().map((e) => ({ date: (e.date || '').slice(5), Active: e.active_users ?? e.dau ?? 0, Actions: e.actions ?? 0, 'AI runs': e.ai_runs ?? 0 }));
  const monthly = {};
  engagement.forEach((e) => { const m = (e.date || '').slice(0, 7); if (!m) return; monthly[m] = monthly[m] || { month: m, logins: 0, actions: 0, ai_runs: 0, days: 0 }; monthly[m].logins += e.logins || 0; monthly[m].actions += e.actions || 0; monthly[m].ai_runs += e.ai_runs || 0; monthly[m].days += 1; });
  const monthlyRows = Object.values(monthly).sort((x, y) => y.month.localeCompare(x.month));
  const topups = credits.filter((c) => c.type === 'topup');
  const usage = credits.filter((c) => c.type !== 'topup');

  const TABS = [
    { k: 'overview', label: 'Overview' }, { k: 'users', label: `Users (${users.length})` },
    { k: 'plan', label: 'Subscription plan' }, { k: 'sites', label: `Sites (${sites.length})` },
    { k: 'credits', label: 'Credits' }, { k: 'engagement', label: 'Engagement' },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subscription-detail">
      <AdminHeader active="subscriptions" />
      <div className="mx-auto max-w-6xl px-5 py-6 sm:px-8">
        <button onClick={() => navigate('/admin/subscriptions')} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> App Registrations</button>

        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><Building2 className="h-6 w-6" /></span>
            <div className="min-w-0">
              <div className="truncate text-lg font-bold text-slate-900" data-testid="sub-name">{a.name || a.external_org_id}</div>
              <div className="truncate text-sm text-slate-500">{a.owner_email || a.domain || '—'}</div>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${a.registration_stage === 'post' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="sub-stage-badge">
                {a.registration_stage === 'post' ? 'Post-Registered' : 'Pre-Registered'}
              </span>
              {a.is_paying && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-bold text-white" data-testid="sub-paying-badge"><BadgeCheck className="h-3.5 w-3.5" /> Paying customer</span>}
              <span className="text-sm font-semibold text-slate-700">{a.plan || '—'}</span>
              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ${STATUS_STYLE[a.plan_status] || 'bg-slate-100 text-slate-600'}`}>{a.plan_status || 'unverified'}</span>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-slate-100 p-1">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`sub-tab-${t.k}`}
              className={`rounded-md px-3.5 py-1.5 text-sm font-semibold transition-all ${tab === t.k ? 'bg-white text-[#0035A8] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>{t.label}</button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="mt-4 space-y-4" data-testid="sub-overview">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Metric label="MRR" value={fmtUSD(a.mrr)} accent="text-[#0035A8]" />
              <Metric label="Bill credits" value={fmtNum(a.credits_balance)} sub="balance" />
              <Metric label="AI credits" value={fmtNum(a.ai_credits_balance)} accent="text-violet-600" sub="balance" />
              <Metric label="Users" value={fmtNum(a.users_count)} />
              <Metric label="Sites" value={fmtNum(a.sites_count)} />
              <Metric label="Accounts" value={fmtNum(a.accounts_count)} />
              <Metric label="Registered" value={fmt(a.registered_at)} />
              <Metric label="Last active" value={fmt(a.last_active_at)} />
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="sub-activity-summary">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Activity className="h-4 w-4 text-[#0035A8]" /> Activity summary <span className="text-xs font-normal text-slate-400">(from the app)</span></div>
              {act ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Metric label="Bills uploaded" value={fmtNum(act.bills_uploaded)} />
                    <Metric label="Active time" value={`${Math.round(act.active_minutes || 0)}m`} />
                    <Metric label="Interactions" value={fmtNum(act.interaction_count)} />
                    <Metric label="Furthest step" value={(act.furthest_step || '—').replace(/_/g, ' ')} accent="text-[#0035A8]" />
                  </div>
                  {act.parsing_status && (
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-700">Parsed: {act.parsing_status.completed ?? 0}</span>
                      <span className="rounded-full bg-amber-50 px-2 py-1 text-amber-700">Processing: {act.parsing_status.processing ?? 0}</span>
                      <span className="rounded-full bg-rose-50 px-2 py-1 text-rose-600">Failed: {act.parsing_status.failed ?? 0}</span>
                    </div>
                  )}
                  {act.interactions && (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(act.interactions).map(([k, v]) => (<span key={k} className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{k.replace(/_/g, ' ')}: <b>{v}</b></span>))}
                    </div>
                  )}
                </div>
              ) : <p className="text-sm text-slate-400">No app activity summary synced yet — it arrives with the next daily sync for trial/usage orgs.</p>}
              {a.app_notes ? <div className="mt-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-700"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">App notes</span><div className="mt-0.5">{a.app_notes}</div></div> : null}
            </div>

            <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-5" data-testid="sub-ai-note">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-violet-800"><Sparkles className="h-4 w-4" /> Astrid's health note</div>
                {canEdit && <button onClick={genAi} disabled={aiLoading} data-testid="sub-ai-generate" className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50">{aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />} {a.ai_health_note ? 'Regenerate' : 'Generate'}</button>}
              </div>
              {a.ai_health_note ? <p className="text-sm text-slate-700">{a.ai_health_note}</p> : <p className="text-sm text-slate-400">Generate a one-line AI summary of where this account is and the best next action.</p>}
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="sub-crm-details">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">CRM details</div>
                {canEdit && !editing && <button onClick={startEdit} data-testid="sub-edit-btn" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0035A8] hover:text-[#0035A8]"><Pencil className="h-3.5 w-3.5" /> Edit</button>}
              </div>
              {editing ? (
                <div className="space-y-3" data-testid="sub-edit-form">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="block"><span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Owner</span>
                      <input value={edit.owner} onChange={(e) => setEdit({ ...edit, owner: e.target.value })} data-testid="sub-edit-owner" className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-[#0035A8]" /></label>
                    <label className="block"><span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Segment</span>
                      <input value={edit.segment} onChange={(e) => setEdit({ ...edit, segment: e.target.value })} data-testid="sub-edit-segment" className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-[#0035A8]" /></label>
                    <label className="block"><span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Company size</span>
                      <input value={edit.size} onChange={(e) => setEdit({ ...edit, size: e.target.value })} data-testid="sub-edit-size" className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-[#0035A8]" /></label>
                    <label className="block"><span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Industry</span>
                      <select value={edit.industry} onChange={(e) => setEdit({ ...edit, industry: e.target.value })} data-testid="sub-edit-industry" className="h-9 w-full rounded-md border border-slate-300 px-2.5 text-sm outline-none focus:border-[#0035A8]">
                        <option value="">—</option>
                        {industries.map((i) => <option key={i.slug} value={i.slug}>{i.label}</option>)}
                      </select></label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditing(false)} data-testid="sub-edit-cancel" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 px-3.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"><X className="h-4 w-4" /> Cancel</button>
                    <button onClick={saveEdit} disabled={savingEdit} data-testid="sub-edit-save" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#0035A8] px-4 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">{savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save</button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                  <div><div className="text-xs font-medium uppercase tracking-wide text-slate-400">Owner</div><div className="mt-0.5 font-medium text-slate-800">{a.owner || '—'}</div></div>
                  <div><div className="text-xs font-medium uppercase tracking-wide text-slate-400">Segment</div><div className="mt-0.5 font-medium text-slate-800">{a.segment || '—'}</div></div>
                  <div><div className="text-xs font-medium uppercase tracking-wide text-slate-400">Size</div><div className="mt-0.5 font-medium text-slate-800">{a.size || '—'}</div></div>
                  <div><div className="text-xs font-medium uppercase tracking-wide text-slate-400">Industry</div><div className="mt-0.5 font-medium text-slate-800">{(industries.find((i) => i.slug === a.industry) || {}).label || a.industry || '—'}</div></div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="mb-2 text-sm font-semibold text-slate-800">CRM notes</div>
              {canEdit && (
                <div className="flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="sub-note-input" className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0035A8]" />
                  <button onClick={addNote} disabled={saving} data-testid="sub-note-save" className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60"><Save className="h-4 w-4" /></button>
                </div>
              )}
              <div className="mt-3 space-y-2">
                {[...(a.crm_notes_log || [])].reverse().map((n) => (<div key={n.id} className="rounded-md border border-slate-100 px-3 py-2 text-sm text-slate-700">{n.text}<div className="text-xs text-slate-400">{n.by} · {fmt(n.at, true)}</div></div>))}
                {(a.crm_notes_log || []).length === 0 ? <p className="text-sm text-slate-400">No notes yet.</p> : null}
              </div>
            </div>
          </div>
        )}

        {tab === 'users' && (
          <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="sub-users">
            <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
              <div className="col-span-4">Name / Email</div><div className="col-span-3">Title</div><div className="col-span-2">Role</div><div className="col-span-3 text-right">Last active</div>
            </div>
            {users.length === 0 ? <div className="flex flex-col items-center gap-2 py-14 text-slate-400"><Users2 className="h-8 w-8" /><p className="text-sm">No users synced.</p></div> :
              users.map((u) => (
                <div key={u.id} className="grid grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3 md:grid-cols-12 md:items-center md:gap-3" data-testid={`sub-user-${u.email}`}>
                  <div className="col-span-4 min-w-0"><div className="truncate text-sm font-semibold text-slate-900">{u.name || u.email}</div><div className="truncate text-xs text-slate-400">{u.email}{u.socials?.linkedin ? <a href={u.socials.linkedin} target="_blank" rel="noreferrer" className="ml-1 text-[#0035A8]">· LinkedIn</a> : null}</div></div>
                  <div className="col-span-3 truncate text-sm text-slate-600">{u.job_title || '—'}</div>
                  <div className="col-span-2"><span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${ROLE_STYLE[u.role] || 'bg-slate-100 text-slate-600'}`}>{u.role || 'user'}</span></div>
                  <div className="col-span-3 text-right text-xs text-slate-400">{fmt(u.last_active_at)}</div>
                </div>
              ))}
          </div>
        )}

        {tab === 'plan' && (
          <div className="mt-4 rounded-lg border border-slate-200 bg-white p-5" data-testid="sub-plan">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><TrendingUp className="h-4 w-4 text-[#0035A8]" /> Plan history</div>
            {plans.length === 0 ? <p className="text-sm text-slate-400">No plan events synced.</p> :
              <div className="space-y-3">
                {plans.map((p) => (
                  <div key={p.id} className="flex gap-3 text-sm" data-testid={`sub-plan-${p.id}`}>
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#0035A8]/50" />
                    <div>
                      <div className="text-slate-800"><span className="font-semibold capitalize">{p.action}</span>{p.prev_plan ? ` — ${p.prev_plan} → ` : ' — '}<span className="font-medium">{p.plan}</span>{p.mrr != null ? ` · ${fmtUSD(p.mrr)}/mo` : ''}</div>
                      <div className="text-xs text-slate-400">{fmt(p.effective_at || p.created_at, true)}{p.note ? ` · ${p.note}` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>}
          </div>
        )}

        {tab === 'sites' && (
          <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="sub-sites">
            <div className="hidden grid-cols-12 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
              <div className="col-span-5">Site</div><div className="col-span-4">Location</div><div className="col-span-2 text-center">Accounts</div><div className="col-span-1 text-center">Meters</div>
            </div>
            {sites.length === 0 ? <div className="flex flex-col items-center gap-2 py-14 text-slate-400"><Layers className="h-8 w-8" /><p className="text-sm">No sites synced.</p></div> :
              sites.map((s) => (
                <div key={s.id} className="grid grid-cols-1 gap-2 border-b border-slate-100 px-5 py-3 md:grid-cols-12 md:items-center md:gap-3">
                  <div className="col-span-5 truncate text-sm font-semibold text-slate-900">{s.name || s.site_ref}</div>
                  <div className="col-span-4 truncate text-sm text-slate-600">{[s.city, s.state].filter(Boolean).join(', ') || s.address || '—'}</div>
                  <div className="col-span-2 text-center text-sm text-slate-700">{s.accounts_count ?? 0}</div>
                  <div className="col-span-1 text-center text-sm text-slate-700">{s.meters_count ?? 0}</div>
                </div>
              ))}
          </div>
        )}

        {tab === 'credits' && (
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="sub-credits">
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Coins className="h-4 w-4 text-[#0035A8]" /> Top-ups ({topups.length})</div>
              {topups.length === 0 ? <p className="text-sm text-slate-400">No top-ups synced.</p> :
                <div className="space-y-2">{topups.map((c) => (<div key={c.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2 text-sm"><div><div className="font-medium text-slate-800">+{fmtNum(c.credits)} credits{c.amount != null ? ` · ${fmtUSD(c.amount)}` : ''}</div><div className="text-xs text-slate-400">{fmt(c.occurred_at || c.created_at)}{c.description ? ` · ${c.description}` : ''}</div></div><span className="text-xs text-slate-500">bal {fmtNum(c.balance_after)}</span></div>))}</div>}
            </div>
            <div className="rounded-lg border border-violet-200 bg-white p-5">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-violet-800"><Sparkles className="h-4 w-4" /> AI &amp; usage ({usage.length})</div>
              {usage.length === 0 ? <p className="text-sm text-slate-400">No usage synced.</p> :
                <div className="space-y-2">{usage.map((c) => (<div key={c.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2 text-sm"><div><div className="font-medium text-slate-800"><span className="capitalize">{(c.type || '').replace('_', ' ')}</span> · {fmtNum(c.credits)} credits</div><div className="text-xs text-slate-400">{fmt(c.occurred_at || c.created_at)}{c.description ? ` · ${c.description}` : ''}</div></div><span className="text-xs text-violet-600">AI bal {fmtNum(c.ai_balance_after)}</span></div>))}</div>}
            </div>
          </div>
        )}

        {tab === 'engagement' && (
          <div className="mt-4 space-y-4" data-testid="sub-engagement">
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Activity className="h-4 w-4 text-[#0035A8]" /> Daily engagement</div>
              {chartData.length === 0 ? <p className="text-sm text-slate-400">No engagement synced.</p> :
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={chartData} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="Active" stroke="#0035A8" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="Actions" stroke="#FF5757" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="AI runs" stroke="#7c3aed" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>}
            </div>
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <div className="grid grid-cols-4 gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <div>Month</div><div className="text-center">Logins</div><div className="text-center">Actions</div><div className="text-center">AI runs</div>
              </div>
              {monthlyRows.length === 0 ? <div className="py-8 text-center text-sm text-slate-400">No monthly data.</div> :
                monthlyRows.map((m) => (
                  <div key={m.month} className="grid grid-cols-4 gap-3 border-b border-slate-100 px-5 py-2.5 text-sm">
                    <div className="font-medium text-slate-800">{m.month}</div>
                    <div className="text-center text-slate-600">{fmtNum(m.logins)}</div>
                    <div className="text-center text-slate-600">{fmtNum(m.actions)}</div>
                    <div className="text-center text-violet-600">{fmtNum(m.ai_runs)}</div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
